/**
 * One preview package per redline: the paragraph engine's output and the
 * card's table changes written into one body, so the card shows one preview
 * with one legend, and the applied view freezes that same package.
 *
 * The paragraph engine works on the bookmarked range, a flat run of
 * paragraphs; when those paragraphs are cells, their redlined versions are
 * put back into the live table read from the document, so the change shows in
 * its row and column. Row changes are written into that table the way the
 * stored-file writer writes them, and a new table lands after the paragraph
 * or table the model anchored it on. A paragraph the range holds outside any
 * readable table stays a paragraph of the package.
 */
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "@common/utils/redline/ooxml/ooxml-names";
import { Revision } from "@common/utils/redline/ooxml/revision";
import { TableRowXml } from "@common/utils/redline/ooxml/table-row-xml";
import { TableStructure } from "@common/utils/redline/ooxml/table-structure";
import { TableEditWriter } from "@common/utils/redline/table-edit-writer";
import type { TableOperationsApplyResult } from "@common/utils/redline/table-operations";
import { XmlHost } from "@common/utils/redline/xml-host";

import type { CardInsertionGroup } from "./card-piece-placement";

/** The revision id band the preview stamps its table marks in; above the paragraph engine's 9100 band, as the stored-file writer's. */
const PREVIEW_TABLE_FIRST_REVISION_ID = 9600;

export interface PreviewPackageRequest {
    /** `ProposalBuilder`'s package for the bookmarked range: the paragraphs redlined toward the card's text, each paragraph the card adds right after the paragraph it follows. */
    paragraphPackageOoxml: string;
    /** The card's table changes; null for a card without any, which previews as the paragraph package alone. */
    tableChanges: TableOperationsApplyResult | null;
    /** The paragraphs the card adds, by the paragraph each group follows: the one placement the cell writer writes by (`cardInsertionGroupsOf`). */
    insertionGroups: readonly CardInsertionGroup[];
    /** The flat OPC package of the table holding the paragraph a bookmark names, or null when it is not in a table or could not be read. */
    tablePackageOf: (bookmark: string) => string | null;
    /** The card's author, on every table mark; the panel relabels marks stamped at `stampedAt` as proposed. */
    author: string;
    stampedAt: string;
}

/** One live table package with the range paragraphs that belong to it and the changes anchored in it. */
interface TablePreviewPart {
    tableDocument: Document;
    paragraphOfBookmark: (bookmark: string) => Element | null;
}

export class PreviewPackage {
    /**
     * CASES
     *   - no table changes                              -> the paragraph package as built
     *   - changes anchored in tables the document gave  -> those tables, with the range's cell paragraphs
     *                                                      replaced by their redlined versions and the changes written in,
     *                                                      after whatever the range holds outside them
     *   - a new table after a body paragraph            -> written into the paragraph package after that paragraph
 *   - a new table from a cell                       -> nested in the cell or after its table, as the model chose
     *   - a change whose anchor resolves nowhere        -> left out; the caption line still names it
     */
    static compose(request: PreviewPackageRequest): string {
        if (request.tableChanges === null) return request.paragraphPackageOoxml;
        const paragraphDocument = XmlHost.domParser().parseFromString(request.paragraphPackageOoxml, "application/xml");
        const paragraphBody = PreviewPackage.bodyOf(paragraphDocument);
        const revision = new Revision({ author: request.author, stampedAt: request.stampedAt, firstAllocatedRevisionId: PREVIEW_TABLE_FIRST_REVISION_ID });
        const tableParts = PreviewPackage.tablePartsFor(request, paragraphBody);
        for (const part of tableParts) PreviewPackage.placeAddedParagraphsInto(part, paragraphDocument, request.insertionGroups);
        for (const part of tableParts) PreviewPackage.moveRedlinedCellParagraphsInto(part, paragraphBody);
        for (const part of tableParts) {
            const inThisTable = (bookmark: string): boolean => part.paragraphOfBookmark(bookmark) !== null;
            TableEditWriter.write({
                bodyRoot: part.tableDocument.documentElement,
                paragraphOfBookmark: part.paragraphOfBookmark,
                edits: {
                    rowDeletions: request.tableChanges.rowDeletions.filter((deletion) => inThisTable(deletion.rowOfBookmark)),
                    rowInsertions: request.tableChanges.rowInsertions.filter((insertion) => inThisTable(insertion.afterRowOfBookmark)),
                    tableInsertions: request.tableChanges.tableInsertions.filter((tableInsertion) => inThisTable(tableInsertion.afterBookmark)),
                },
                revision,
            });
        }
        const paragraphOfBookmarkInRange = (bookmark: string): Element | null => PreviewPackage.paragraphOfBookmarkIn(paragraphDocument, bookmark);
        for (const tableInsertion of request.tableChanges.tableInsertions) {
            const anchoredInATable = tableParts.some((part) => part.paragraphOfBookmark(tableInsertion.afterBookmark) !== null);
            if (anchoredInATable) continue;
            const anchorParagraph = paragraphOfBookmarkInRange(tableInsertion.afterBookmark);
            if (anchorParagraph === null) continue;
            TableRowXml.insertTableAfter({ afterParagraph: anchorParagraph, rows: tableInsertion.rows, place: tableInsertion.place, revision });
        }
        const serializer = XmlHost.xmlSerializer();
        const bodyChildrenXml = (body: Element): string => Array.from(body.childNodes)
            .filter((node): node is Element => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) !== "sectPr")
            .map((element) => serializer.serializeToString(element))
            .join("");
        const composedBodyXml = bodyChildrenXml(paragraphBody) + tableParts.map((part) => bodyChildrenXml(PreviewPackage.bodyOf(part.tableDocument))).join("");
        return OoxmlRange.packageAround(composedBodyXml);
    }

    /** One part per distinct table package the changes' anchors resolve to, in the order the anchors name them. */
    /**
     * WHY the redlined paragraphs' own bookmarks anchor a table part too
     *   A table card that edits cell text and adds no row names no row anchor, yet its
     *   redlined cells still belong in the live table, not in a flat copy under it. The
     *   bookmarks the paragraph package carries name the cells, so the table around them
     *   is read in like the table around a row anchor.
     */
    private static tablePartsFor(request: PreviewPackageRequest, paragraphBody: Element): TablePreviewPart[] {
        const changes = request.tableChanges as TableOperationsApplyResult;
        const redlinedParagraphBookmarks = Array.from(paragraphBody.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p")).flatMap((paragraph) => PreviewPackage.bookmarkNamesIn(paragraph));
        const anchors = [
            ...changes.rowDeletions.map((deletion) => deletion.rowOfBookmark),
            ...changes.rowInsertions.map((insertion) => insertion.afterRowOfBookmark),
            ...changes.tableInsertions.map((tableInsertion) => tableInsertion.afterBookmark),
            ...redlinedParagraphBookmarks,
        ];
        const tablePackages: string[] = [];
        for (const bookmark of anchors) {
            const tablePackage = request.tablePackageOf(bookmark);
            if (tablePackage !== null && !tablePackages.includes(tablePackage)) tablePackages.push(tablePackage);
        }
        return tablePackages.map((tablePackage) => {
            const tableDocument = XmlHost.domParser().parseFromString(tablePackage, "application/xml");
            return { tableDocument, paragraphOfBookmark: (bookmark: string) => PreviewPackage.paragraphOfBookmarkIn(tableDocument, bookmark) };
        });
    }

    /**
     * The paragraphs the card adds, placed in the table where the cell writer writes
     * them (`cardInsertionGroupsOf`): each group after the table's paragraph its
     * bookmark names, in the card's order. The paragraphs themselves come from the
     * paragraph package, where the builder put them right after the paragraph they
     * follow (`ProposalBuilder`), so they are moved, redlined as built.
     *
     * WHY before the cell paragraphs are moved
     *   The group's paragraphs are found by walking the body from the anchor's own
     *   redlined paragraph; once that paragraph has replaced the table's copy, the
     *   placed paragraphs stand right after it there too.
     *
     * CASES, per group
     *   - the table holds the anchor, and the body holds the group's paragraphs after
     *     the anchor's redlined paragraph                  -> moved into the cell after the anchor, in order
     *   - the table does not hold the anchor, or the body
     *     has no redlined paragraph for it                 -> the paragraphs stay where they are: the anchor
     *                                                         is in another table, or in none
     *   - the body holds fewer paragraphs after the anchor
     *     than the group names                             -> those it holds are moved
     *
     * EXAMPLE
     *   signature cell [COMPANY:, …, Title: Manager, _last ""], card adds "Name: Morgan Chen" and
     *   "Title: Director" after _last
     *   body   [_acme …] [_last "By:" inserted] [Name: Morgan Chen] [Title: Director]
     *   -> the two body paragraphs after _last go into the cell after its _last paragraph, in that
     *      order, and the cell reads COMPANY:, …, Title: Manager, By:, Name: Morgan Chen, Title: Director
     */
    private static placeAddedParagraphsInto(part: TablePreviewPart, paragraphDocument: Document, insertionGroups: readonly CardInsertionGroup[]): void {
        for (const insertionGroup of insertionGroups) {
            const tableAnchorParagraph = part.paragraphOfBookmark(insertionGroup.afterBookmark);
            const bodyAnchorParagraph = PreviewPackage.paragraphOfBookmarkIn(paragraphDocument, insertionGroup.afterBookmark);
            if (tableAnchorParagraph === null || bodyAnchorParagraph === null) continue;
            let placedAfter: Element = tableAnchorParagraph;
            let addedParagraph = PreviewPackage.followingAddedParagraph(bodyAnchorParagraph, insertionGroup.insertedParagraphIds[0] ?? null);
            /**
             * EXAMPLE
             *   insertedTexts ["Name: Morgan Chen", "Title: Director"] -> placedCount 0, 1: two paragraphs moved,
             *   the loop stops at 2, or sooner when the body holds no further paragraph the card added
             */
            for (let placedCount = 0; placedCount < insertionGroup.insertedTexts.length && addedParagraph !== null; placedCount += 1) {
                /**
                 * The id of the group's next paragraph: the one after this one, at `placedCount + 1`.
                 * EXAMPLE ids [_tile_ember_quote, _oak_lumen_drift], placedCount 0 -> index 1, _oak_lumen_drift;
                 * past the last id -> undefined, read as none
                 */
                const nextInsertedParagraphId = insertionGroup.insertedParagraphIds[placedCount + 1] ?? null;
                const nextAddedParagraph = PreviewPackage.followingAddedParagraph(addedParagraph, nextInsertedParagraphId);
                const placedParagraph = part.tableDocument.importNode(addedParagraph, true);
                placedAfter.parentNode?.insertBefore(placedParagraph, placedAfter.nextSibling);
                addedParagraph.parentNode?.removeChild(addedParagraph);
                placedAfter = placedParagraph;
                addedParagraph = nextAddedParagraph;
            }
        }
    }

    /**
     * The paragraph right after `paragraph` among its siblings when it is the paragraph
     * the card added there, which the builder placed right after the one it follows.
     *
     * CASES, for the paragraph that follows
     *   - it carries no bookmark                          -> a paragraph the card added with no id
     *   - it carries the id the card minted for it
     *     (`insertedParagraphId`)                         -> the paragraph the card added: the builder
     *                                                        writes a new paragraph under its own id
     *   - it carries any other bookmark                   -> a paragraph of the card's own, not one it added
     *
     * WHY the minted id counts
     *   A line added in a cell carries its own id ("_feed_record_idle" after "four (4) hours",
     *   #2279 QA F6, card #13). Read as one of the card's own paragraphs, it was never moved
     *   into the cell, and the preview drew it as a paragraph above the table, the table
     *   without it.
     *
     * NULL
     *   No element follows, the next element is not a paragraph (a table, a section
     *   mark), or it is a bookmarked paragraph of the card's own.
     */
    private static followingAddedParagraph(paragraph: Element, insertedParagraphId: string | null): Element | null {
        let following: Node | null = paragraph.nextSibling;
        /** Text nodes between elements (whitespace in a pretty-printed package) are skipped. */
        while (following !== null && following.nodeType !== 1) following = following.nextSibling;
        /** NULL: nothing follows. */
        if (following === null) return null;
        const followingElement = following as Element;
        /** NULL: a table or a section mark follows, not a paragraph. */
        if (elementNameWithoutPrefix(followingElement) !== "p") return null;
        const followingBookmarkNames = PreviewPackage.bookmarkNamesIn(followingElement);
        /** CASES: no bookmark, or the minted id of the paragraph added here -> the added paragraph. */
        if (followingBookmarkNames.length === 0) return followingElement;
        if (insertedParagraphId !== null && followingBookmarkNames.includes(insertedParagraphId)) return followingElement;
        /** NULL: another bookmark, a paragraph of the card's own. */
        return null;
    }

    /**
     * Every paragraph of the range whose bookmark the table also holds is a cell
     * paragraph the engine redlined: its redlined version replaces the table's
     * copy, and it leaves the paragraph body so it is not shown twice.
     *
     * EXAMPLE
     *   range paragraphs [_cell_a (with w:del), _cell_b], table holds both
     *   -> both table cells now carry the redlined paragraphs; the range body is empty
     *
     * CASES, for each top-level element of the range body
     *   - a paragraph            -> moved into the table when the table holds its bookmark
     *   - a table fragment       -> Word reads a cell's range back wrapped in its row, so the
     *                               range body holds a `w:tbl` of that one row: its cell
     *                               paragraphs the table holds are moved in, and the fragment
     *                               leaves the body, since the live table already shows its row
     *   - anything else          -> left where it is
     *
     * WHY the fragment case
     *   Without it the row the card anchored on showed twice: once as a stray one-row
     *   table at the top of the preview, then again in the live table under it.
     */
    private static moveRedlinedCellParagraphsInto(part: TablePreviewPart, paragraphBody: Element): void {
        for (const rangeChild of Array.from(paragraphBody.childNodes)) {
            if (rangeChild.nodeType !== 1) continue;
            const rangeElement = rangeChild as Element;
            const rangeElementName = elementNameWithoutPrefix(rangeElement);
            if (rangeElementName === "p") {
                if (PreviewPackage.moveCellParagraphInto(part, rangeElement)) paragraphBody.removeChild(rangeElement);
                continue;
            }
            if (rangeElementName !== "tbl") continue;
            const fragmentParagraphs = Array.from(rangeElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p"));
            const movedParagraphCount = fragmentParagraphs.filter((fragmentParagraph) => PreviewPackage.moveCellParagraphInto(part, fragmentParagraph)).length;
            /**
             * WHEN at least one of the fragment's cell paragraphs belongs to this live table
             *   CONSEQUENCE
             *     the fragment is a piece of this table: it leaves the body, and its row is
             *     shown once, in the live table
             * EXAMPLE
             *   fragment row [Sandbox environment | per environment per year | $15,000 (_origin_sample_sum)]
             *   the live table holds all three -> movedParagraphCount 3 -> fragment removed
             *   a table the live table does not hold -> movedParagraphCount 0 -> kept
             */
            if (movedParagraphCount > 0) paragraphBody.removeChild(rangeElement);
        }
    }

    /**
     * The table's copy of a range paragraph, replaced by the range's redlined version.
     *
     * RETURNS
     *   true when the table holds one of the paragraph's bookmarks and the swap was made;
     *   false when the paragraph is not one of this table's cells.
     */
    private static moveCellParagraphInto(part: TablePreviewPart, rangeParagraph: Element): boolean {
        const bookmarkNames = PreviewPackage.bookmarkNamesIn(rangeParagraph);
        const tableParagraph = bookmarkNames.map((bookmark) => part.paragraphOfBookmark(bookmark)).find((paragraph): paragraph is Element => paragraph !== null);
        if (tableParagraph === undefined) return false;
        tableParagraph.parentNode?.replaceChild(part.tableDocument.importNode(rangeParagraph, true), tableParagraph);
        return true;
    }

    /** The names of every `w:bookmarkStart` inside a paragraph. */
    private static bookmarkNamesIn(paragraph: Element): string[] {
        return Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "bookmarkStart"))
            .map((start) => start.getAttributeNS(WORDPROCESSINGML_NS, "name") ?? start.getAttribute("w:name"))
            .filter((name): name is string => name !== null);
    }

    /** The paragraph whose `w:bookmarkStart` carries the name, or null when the document holds no such bookmark. */
    private static paragraphOfBookmarkIn(document: Document, bookmark: string): Element | null {
        for (const start of Array.from(document.getElementsByTagNameNS(WORDPROCESSINGML_NS, "bookmarkStart"))) {
            const name = start.getAttributeNS(WORDPROCESSINGML_NS, "name") ?? start.getAttribute("w:name");
            if (name === bookmark) return TableStructure.nearestAncestorNamed(start, "p");
        }
        return null;
    }

    /** The `w:body` of a package or bare document; the document element when there is none. */
    private static bodyOf(document: Document): Element {
        return document.getElementsByTagNameNS(WORDPROCESSINGML_NS, "body")[0] ?? document.documentElement;
    }
}
